import { 
  Calculator, 
  FileText, 
  LayoutDashboard,
  HandCoins,
  Coins,
  TrendingUp,
  Factory,
  Building,
  Users,
  FileBarChart,
  Receipt,
  Bot,
  Heart,
  Briefcase,
  Settings,
  ShoppingCart,
  CreditCard,
  UserCheck,
  Landmark,
  ClipboardCheck,
  BookOpen,
} from 'lucide-react';
import React from 'react';

export interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  color?: string;
  /** { resource, action } — if set, menu only shows when user.can(resource, action) */
  requiredPermission?: { resource: string; action: string };
  subItems?: { label: string; href: string; separator?: boolean }[];
}

export const MENU_ITEMS: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '#dashboard',
    color: '#10b981'
  },
  {
    id: 'accounts-receivable',
    label: 'Accounts Receivable',
    icon: HandCoins,
    href: '#accounts-receivable',
    color: '#f59e0b',
    requiredPermission: { resource: 'customers', action: 'view' },
    subItems: [
      { label: 'Customers', href: '#ar-customers' },
      { label: 'Invoicing', href: '#ar-invoicing' },
      { label: 'Payments', href: '#ar-payments' },
      { label: 'Credit & Collections', href: '#ar-credit' },
      { label: 'Reports & Aging', href: '#ar-reports' },
    ]
  },
  {
    id: 'accounts-payable',
    label: 'Accounts Payable',
    icon: FileText,
    href: '#accounts-payable',
    color: '#8b5cf6',
    requiredPermission: { resource: 'bills', action: 'view' },
    subItems: [
      { label: 'Procurement', href: '#ap-procurement' },
      { label: 'Bills & Payments', href: '#ap-bills' },
      { label: 'Vendors', href: '#ap-vendors' },
      { label: 'Banking', href: '#ap-banking' },
      { label: 'Reports & Control', href: '#ap-reports' },
    ]
  },
  {
    id: 'general-ledger',
    label: 'General Ledger',
    icon: LayoutDashboard,
    href: '#general-ledger',
    color: '#3b82f6',
    requiredPermission: { resource: 'accounts', action: 'view' },
    subItems: [
      { label: 'Chart of Accounts', href: '#chart-accounts' },
      { label: 'Journal Entries', href: '#journal-entries' },
      { label: 'Trial Balance', href: '#trial-balance' },
      { label: 'Account Reconciliation', href: '#account-recon' },
      { label: 'Recurring Transactions', href: '#recurring-transactions' },
      { label: 'Period Closing', href: '#period-closing' },
      { label: 'Parallel Accounting', href: '#parallel-accounting' }
    ]
  },
  {
    id: 'cash-management',
    label: 'Cash Management',
    icon: Coins,
    href: '#cash-management',
    color: '#facc15',
    requiredPermission: { resource: 'accounts', action: 'view' },
    subItems: [
      { label: 'Cash Position', href: '#cash-position' },
      { label: 'Cash Flow Dashboard', href: '#unified-cashflow' },
      { label: 'Treasury Management', href: '#treasury' },
      { label: 'Payment Matching', href: '#payment-matching' },
      { label: 'Vendor-Customer Netting', href: '#netting' },
      { label: 'Cash Flow Forecasting', href: '#cash-flow-forecast' },
      { label: 'Bank Accounts', href: '#bank-accounts' },
      { label: 'Cash Transactions', href: '#cash-transactions' }
    ]
  },
  {
    id: 'cost-management',
    label: 'Cost Management',
    icon: Calculator,
    href: '#cost-management',
    color: '#ef4444',
    requiredPermission: { resource: 'expenses', action: 'view' },
    subItems: [
      { label: 'Cost Dashboard', href: '#cost-dashboard' },
      { label: 'Cost Centers', href: '#cost-centers' },
      { label: 'Cost Tracking', href: '#cost-tracking' },
      { label: 'Cost Analysis', href: '#cost-analysis' }
    ]
  },
  {
    id: 'budgeting',
    label: 'Budgeting',
    icon: TrendingUp,
    href: '#budgeting',
    color: '#10b981',
    requiredPermission: { resource: 'reports', action: 'view' },
    subItems: [
      { label: 'Budget Dashboard', href: '#budget-dashboard' },
      { label: 'Budget Planning', href: '#budget-planning' },
      { label: 'Budget Monitoring', href: '#budget-monitoring' },
      { label: 'Budget Analysis', href: '#budget-analysis' }
    ]
  },
  {
    id: 'inventory',
    label: 'Inventory',
    icon: Factory,
    href: '#inventory',
    color: '#ec4899',
    requiredPermission: { resource: 'inventory', action: 'view' },
    subItems: [
      { label: 'Inventory Dashboard', href: '#inventory-dashboard' },
      { label: 'Products & Stock', href: '#products-stock' },
      { label: 'Warehouse', href: '#warehouse' },
      { label: 'Inventory Analysis', href: '#inventory-analysis' }
    ]
  },
  {
    id: 'fixed-assets',
    label: 'Fixed Assets',
    icon: Building,
    href: '#fixed-assets',
    color: '#60a5fa',
    requiredPermission: { resource: 'fixed_assets', action: 'view' },
    subItems: [
      { label: 'Asset Register', href: '#asset-register' },
      { label: 'Depreciation', href: '#depreciation' },
      { label: 'Asset Disposal', href: '#asset-disposal' },
      { label: 'Asset Reports', href: '#asset-reports' }
    ]
  },
  {
    id: 'payroll',
    label: 'Payroll',
    icon: Users,
    href: '#payroll',
    color: '#a78bfa',
    requiredPermission: { resource: 'payroll', action: 'view' },
    subItems: [
      { label: 'Employee Management', href: '#employee-mgmt' },
      { label: 'Time & Attendance', href: '#time-attendance' },
      { label: 'Payroll Processing', href: '#payroll-processing' },
      { label: 'Benefits Management', href: '#benefits-mgmt' },
      { label: 'Tax & Compliance', href: '#tax-compliance' },
      { label: 'Payroll Reports', href: '#payroll-reports' },
      { label: 'Payroll Settings', href: '#payroll-settings' },
      { label: 'AI Analytics', href: '#ai-analytics' },
      { label: 'Workflow Automation', href: '#workflow-automation' },
      { label: 'Tax Scraper Management', href: '#tax-scraper-mgmt' }
    ]
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: FileBarChart,
    href: '#reports',
    color: '#3b82f6',
    requiredPermission: { resource: 'reports', action: 'view' },
    subItems: [
      { label: 'Financial Statements', href: '#financial-statements' },
      { label: 'Statement of Equity', href: '#statement-equity' },
      { label: 'Financial Ratios & Analysis', href: '#financial-ratios' },
      { label: 'AI Financial Insights', href: '#ai-insights' },
      { label: 'Custom Report Builder', href: '#custom-reports' },
      { label: 'Management Dashboard', href: '#mgmt-dashboard' }
    ]
  },
  {
    id: 'tax-management',
    label: 'Tax Management',
    icon: Receipt,
    href: '#tax-management',
    color: '#ef4444',
    requiredPermission: { resource: 'reports', action: 'view' },
    subItems: [
      { label: 'Tax Setup', href: '#tax-setup' },
      { label: 'Tax Calculations', href: '#tax-calculations' },
      { label: 'Tax Returns', href: '#tax-returns' },
      { label: 'Tax Reports', href: '#tax-reports' }
    ]
  },
  {
    id: 'ai-assistant',
    label: 'AI Assistant',
    icon: Bot,
    href: '#ai-assistant',
    color: '#a78bfa',
    requiredPermission: { resource: 'ai_assistant', action: 'view' },
    subItems: [
      { label: 'Smart Analysis', href: '#smart-analysis' },
      { label: 'Automated Reports', href: '#automated-reports' },
      { label: 'Predictive Analytics', href: '#predictive-analytics' },
      { label: 'AI Recommendations', href: '#ai-recommendations' }
    ]
  },
  {
    id: 'internal-control',
    label: 'Internal Control',
    icon: Heart,
    href: '#internal-control',
    color: '#94a3b8',
    requiredPermission: { resource: 'reports', action: 'view' },
    subItems: [
      { label: 'Real-time Alerts', href: '#alerts-dashboard' },
      { label: 'Risk Assessment', href: '#risk-assessment' },
      { label: 'Fraud Detection', href: '#fraud-detection' },
      { label: 'Audit Trail', href: '#audit-trail' },
      { label: 'Compliance Monitoring', href: '#compliance-monitoring' },
      { label: 'Control Testing', href: '#control-testing' }
    ]
  },
  {
    id: 'business-intelligence',
    label: 'Business Intelligence',
    icon: Briefcase,
    href: '#business-intelligence',
    color: '#94a3b8',
    requiredPermission: { resource: 'reports', action: 'view' },
    subItems: [
      { label: 'Business Intelligence', href: '#bi' },
      { label: 'Dashboard Analytics', href: '#dashboard-analytics' },
      { label: 'KPI Monitoring', href: '#kpi-monitoring' },
      { label: 'Performance Metrics', href: '#performance-metrics' },
      { label: 'Data Visualization', href: '#data-viz' }
    ]
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    href: '#settings',
    color: '#94a3b8',
    requiredPermission: { resource: 'settings', action: 'view' },
    subItems: [
      { label: 'Settings', href: '#settings-main' },
      { label: 'Company Settings', href: '#company-settings' },
      { label: 'User Management', href: '#user-mgmt' },
      { label: 'Currency Management', href: '#currency-mgmt' },
      { label: 'Tax Rules', href: '#tax-rules' },
      { label: 'Integrations', href: '#integrations' },
      { label: 'Plan & Billing', href: '#plan-billing' },
      { label: 'System Configuration', href: '#sys-config' },
      { label: 'Data Management', href: '#data-mgmt' },
      { label: 'Backup & Restore', href: '#backup-restore' }
    ]
  },
  {
    id: 'help',
    label: 'Help Center',
    icon: BookOpen,
    href: '#help',
    color: '#3b82f6',
  }
];
